// Before anything else: a dashboard opened on ng serve's 127.0.0.1 address moves to its portless URL.
import './portless-redirect';
// Side-effect import: initializes the Firebase app, App Check and Auth at module
// evaluation, before any service calls getAuth()/getDatabase()/getFirestore().
import './firebase-init';
import { enableProdMode, importProvidersFrom, inject, provideAppInitializer, provideCheckNoChangesConfig, provideZonelessChangeDetection } from '@angular/core';
import { VersionedHttpLoader, appInitializerFactory } from './app/app.config';
import { environment } from 'environment';
import { AppComponent } from './app/app.component';
import { ToastrModule } from 'ngx-toastr';
import { provideRouter, withRouterConfig } from '@angular/router';
import { bootstrapApplication } from '@angular/platform-browser';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { TranslateService, TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { HttpClient, provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { MaintenanceService } from '@services/auth/maintenance.service';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
import routes from './app/routes/global.routes';
import { errorInterceptor } from './app/interceptors/error.interceptor';
import { headerInterceptor } from './app/interceptors/header.interceptor';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

if (environment.production || window.location.hostname.includes('sandbox.admin.airwallet.net') || window.location.hostname.includes('demo.airwallet.net')) {
    enableProdMode();
    if (!window.location.hostname.includes('localhost') && !window.location.hostname.includes('sandbox.admin.airwallet.net')) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        window.console.log = function () {};
    }
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZonelessChangeDetection(),
        // CDK 22 renders overlays (modals, drawers, menus, tooltips) in the browser top layer via
        // popover by default, which paints above every z-index, so toasts ended up under modal
        // backdrops. Opt out to keep the z-index scale in styles.scss in charge of stacking.
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        // Zoneless prep: periodically run an exhaustive checkNoChanges pass in dev
        // so views that go stale without a change-detection notifier (signal write,
        // template event, markForCheck) throw instead of silently not updating.
        // No-op in production builds — checkNoChanges only runs in dev mode.
        ...(environment.production ? [] : [provideCheckNoChangesConfig({ exhaustive: true, interval: 1000 })]),
        provideNgxSkeletonLoader({
            theme: {
                extendsFromRoot: true,
                'margin-bottom': '0px'
            }
        }),
        importProvidersFrom(
            ToastrModule.forRoot({
                preventDuplicates: true,
                //Global setting to give all toast 7 seconds
                timeOut: 7000,
                //Make toast go away after 1 second when not hovering over it
                extendedTimeOut: 1000
            }),
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: () => new VersionedHttpLoader(inject(HttpClient)),
                    deps: [HttpClient]
                }
            })
        ),
        provideLottieOptions({
            player: () => player
        }),
        HttpClient,
        provideAppInitializer(() => {
            const initializerFn = appInitializerFactory(inject(TranslateService), inject(LocalStorageService));
            return initializerFn();
        }),
        provideAppInitializer(() => inject(MaintenanceService).prefetched),
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
        provideHttpClient(withXhr(), withInterceptors([headerInterceptor, errorInterceptor])),
        // The document doesn't scroll — `.main-content-item` does — so
        // `withInMemoryScrolling` would be a no-op. The app shell resets
        // the inner scroll surface on `NavigationEnd` instead
        // (`AppComponent.scrollContainerToTop`).
        // v22 flipped the default paramsInheritanceStrategy to 'always'; pin the
        // pre-22 behavior so parent params/data don't leak into child routes.
        provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }))
    ]
}).catch(err => console.error(err));
